import { useEffect, useMemo, useState } from "react";

import { apiRequest } from "../api";

import Swal from "sweetalert2";

function formatDateInput(value) {
  if (!value) return "";
  const str = String(value);
  return str.includes("T") ? str.slice(0, 10) : str;
}

function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
}

function addCurrentValue(options, currentValue) {
  if (!currentValue) return options;

  return options.includes(currentValue)
    ? options
    : [currentValue, ...options];
}

function formatOptionLabel(item, fallbackKey = "code", fallbackLabel = "description") {
  const code = item?.code || item?.value || item?.[fallbackKey] || "";
  const label = item?.description || item?.label || item?.[fallbackLabel] || "";
  return label ? `${code} - ${label}` : code;
}

function InvoiceItemsTable({ items, onChangeTaxCode, gstCodes }) {
  if (!Array.isArray(items) || !items.length) {
    return <p className="empty-state">No items found</p>;
  }

  const headers = Object.keys(items[0] || {});

  return (
    <div className="invoice-edit-modal__table-wrap">
      <table className="invoice-edit-modal__table">
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, rowIndex) => (
            <tr key={item.id ?? rowIndex}>
              {headers.map((header) => (
                <td key={`${rowIndex}-${header}`}>
                  {header === "TaxCode" ? (
                    <select
                      className="invoice-edit-modal__select invoice-edit-modal__select--compact"
                      value={item[header] ?? ""}
                      onChange={(event) => onChangeTaxCode(rowIndex, event.target.value)}
                    >
                      <option value="">Select GST Tax Code</option>
                      {gstCodes.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </select>
                  ) : (
                    String(item[header] ?? "")
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ProcessorInvoiceEditModal({ invoiceGroupKey, tab, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [items, setItems] = useState([]);
  const [section1, setSection1] = useState({
    InvoiceDate: "",
    Narration: "",
    WithholdingTaxCode: "",
    material_service: "",
    tds_value: "",
  });
  const [section4, setSection4] = useState({
    ExchangeRate: "",
    CurrentExchangeRate: "",
    exchange_rate_user: "",
  });
  const [section3, setSection3] = useState({
    InvoiceValue: "",
    InvoiceValueMigo: "",
    unallocatedCost: 0,

  });
  const [withholdingTaxCodes, setWithholdingTaxCodes] = useState([]);
  const [gstTaxCodes, setGstTaxCodes] = useState([]);
  const [glCodes, setGlCodes] = useState([]);
  const [activeSection, setActiveSection] = useState("overview");
  const [unplannedForm, setUnplannedForm] = useState(null);
  const [unplannedValues, setUnplannedValues] = useState({});
  const [section3Error, setSection3Error] = useState("");

  const title = useMemo(() => {
    return tab === "no_data" ? "Edit Missing Invoice" : "Edit Invoice";
  }, [tab]);

  // Close modal on Esc
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    if (!invoiceGroupKey) return;

    let cancelled = false;
    async function loadDetails() {
      setLoading(true);
      setError("");
      try {
        const payload = await apiRequest("/get-invoice-details/", {
          method: "POST",
          body: {
            InvoiceGroupKey: invoiceGroupKey,
            Tab: tab,
          },
        });

        if (cancelled) return;
        const data = payload.data || payload;
        setInvoice(data);
        setItems(Array.isArray(data.TaxCode) ? data.TaxCode : []);
        console.log(data.TaxCode);
        
        setSection1({
          InvoiceDate: formatDateInput(data.InvoiceDate),
          Narration: data.Narration || "",
          WithholdingTaxCode: data.WithholdingTaxCode || "",
          material_service: data.material_service || "",
          tds_value: data.tds_value || "",
        });
        await loadUnplannedCostForm(data.material_service);
        setSection3({
          InvoiceValue: data.InvoiceValue || "",
          InvoiceValueMigo: data.InvoiceValueMigo || "",
          unplanned_cost: data.unplanned_cost || "",
          unallocatedCost: data.unallocatedCost || 0,
          gl_code: "",
        });
        setSection4({
          ExchangeRate: data.ExchangeRate || "",
          CurrentExchangeRate: data.CurrentExchangeRate || "",
          exchange_rate_user: "",
        });

        const vendorCode = (data.VendorCode || "").trim();
        const [glPayload, wtPayload, gstPayload] = await Promise.all([
          apiRequest("/unallocatedcost-gl-list/"),
          vendorCode ? apiRequest(`/fetch-withholdingtax/?vendor_code=${encodeURIComponent(vendorCode)}`) : Promise.resolve([]),
          vendorCode ? apiRequest(`/fetch-gsttax/?vendor_code=${encodeURIComponent(vendorCode)}`) : Promise.resolve([]),
        ]);

        if (cancelled) return;
        setGlCodes(normalizeList(glPayload));
        setWithholdingTaxCodes(
          addCurrentValue(
            normalizeList(wtPayload),
            data.WithholdingTaxCode
          )
        );
        setGstTaxCodes(
          addCurrentValue(
            normalizeList(gstPayload),
            data.TaxCode[0].TaxCode
          )
        );
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || "Unable to load invoice details.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }


    loadDetails();
    return () => {
      cancelled = true;
    };
  }, [invoiceGroupKey, tab]);

  function updateUnplannedField(field, value) {
    setUnplannedValues((current) => ({
      ...current,
      [field]: value,
    }));
  }

  async function loadUnplannedCostForm(materialService) {
    console.log("called", "/api/invoice/unplanned-cost-form/");
    console.log("invoiceGroupKey", invoiceGroupKey);
    console.log("materialService", materialService);
    
    try {
      const payload = await apiRequest(
        "/invoice/unplanned-cost-form/",
        {
          method: "POST",
          body: {
            invoice_keys: invoiceGroupKey,
            material_service: materialService,
          },
        }
      );

      setUnplannedForm(payload);

      const defaults = {};

      Object.values(payload.normal_fields || {}).forEach((field) => {
        defaults[field.field] = field.default || "";
      });

      Object.values(payload.grn_fields || {}).forEach((field) => {
        defaults[field.field] = field.default || "";
      });

      Object.values(payload.po_fields || {}).forEach((field) => {
        defaults[field.field] = field.default || "";
      });

      defaults.amount = "";

      setUnplannedValues(defaults);
    } catch (error) {
      console.error(error);
      setSection3Error(
        error.message || "Unable to load unplanned cost form."
      );
    }
  }

  function updateItemTaxCode(rowIndex, value) {
    setItems((current) =>
      current.map((item, index) => (index === rowIndex ? { ...item, TaxCode: value } : item)),
    );
  }

  function updateSection1(field, value) {
    setSection1((current) => ({ ...current, [field]: value }));
  }

  // function updateSection3(field, value) {
  //   setSection3((current) => ({ ...current, [field]: value }));
  // }

  function updateSection4(field, value) {
    setSection4((current) => ({ ...current, [field]: value }));
  }

  async function saveSection1() {
    setSaving(true);
    setError("");
    try {
      await apiRequest("/update-invoice-details/", {
        method: "POST",
        body: {
          InvoiceGroupKey: invoiceGroupKey,
          section: 1,
          ...section1,
        },
      });
      onSaved?.();
      onClose();
    } catch (requestError) {
      setError(requestError.message || "Unable to save section 1.");
    } finally {
      setSaving(false);
    }
  }

  async function saveSection2() {
    setSaving(true);
    setError("");
    try {
      await apiRequest("/save-updated-taxcode/", {
        method: "POST",
        body: {
          invoice_id: invoice?.InvoiceId,
          items: items.map((item) => ({ id: item.id, TaxCode: item.TaxCode })),
        },
      });
      onSaved?.();
      onClose();
    } catch (requestError) {
      setError(requestError.message || "Unable to save section 2.");
    } finally {
      setSaving(false);
    }
  }

  async function saveSection3() {
    setSaving(true);
    setError("");

    try {

      const payload = {
        InvoiceGroupKey: invoiceGroupKey,
        section: 3,
        ...section3,
        ...unplannedValues,
      };

      console.log("UNPLANNED COST PAYLOAD");
      console.log(payload);

      await apiRequest("/create-unallocatedcost/", {
        method: "POST",
        body: payload,
      });

      await Swal.fire({
        icon: "success",
        title: "Unplanned Cost Created",
        text: "Line item has been created successfully.",
        confirmButtonText: "OK",
      });

      onSaved?.();
      onClose();

    } catch (requestError) {
      setError(requestError.message || "Unable to save section 3.");
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text:
          requestError.message ||
          "Unable to save unplanned cost.",
      });
    } finally {
      setSaving(false);
    }
  }

  async function saveSection4() {
    setSaving(true);
    setError("");
    try {
      await apiRequest("/update-invoice-details/", {
        method: "POST",
        body: {
          InvoiceGroupKey: invoiceGroupKey,
          section: 4,
          exchange_edit: section4.exchange_rate_user,
        },
      });
      onSaved?.();
      onClose();
    } catch (requestError) {
      setError(requestError.message || "Unable to save section 4.");
    } finally {
      setSaving(false);
    }
  }

  if (!invoiceGroupKey) return null;

  return (
    <div className="processor-modal" role="presentation" onClick={onClose}>
        <div
        className="processor-modal__card processor-modal__card--wide invoice-edit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-edit-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="invoice-edit-modal__hero">
          <div>
            <p className="eyebrow">Invoice Editor</p>
            <h2 id="invoice-edit-title">{title}</h2>
            <p className="invoice-edit-modal__subtitle">
              Update header fields, tax codes, cost allocation, and exchange rate from one place.
            </p>
          </div>
          <button className="ghost-button invoice-edit-modal__close" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        {loading ? <p className="config-note">Loading invoice details...</p> : null}
        {error ? <div className="config-banner config-banner--error">{error}</div> : null}

        {!loading && invoice ? (
          <div className="invoice-edit-modal__shell">
            <div className="invoice-edit-modal__tabs" role="tablist" aria-label="Invoice edit sections">
              {[
                ["overview", "Overview"],
                ["items", "Invoice Items"],
                ["cost", "Cost & GL"],
                ["exchange", "Exchange Rate"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`invoice-edit-modal__tab ${activeSection === key ? "is-active" : ""}`}
                  onClick={() => setActiveSection(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            <section className={`invoice-edit-modal__section ${activeSection === "overview" ? "is-visible" : ""}`}>
              <h3>Section 1 - Basic Info</h3>
              <div className="invoice-edit-modal__grid">
                <label>
                  Invoice Date
                  <input
                    className="invoice-edit-modal__input"
                    type="date"
                    value={section1.InvoiceDate}
                    onChange={(event) => updateSection1("InvoiceDate", event.target.value)}
                  />
                </label>
                <label>
                  Narration
                  <input
                    className="invoice-edit-modal__input"
                    type="text"
                    value={section1.Narration}
                    onChange={(event) => updateSection1("Narration", event.target.value)}
                  />
                </label>
                <label>
                  Withholding Tax Code
                  <select
                    className="invoice-edit-modal__select"
                    value={section1.WithholdingTaxCode}
                    onChange={(event) => updateSection1("WithholdingTaxCode", event.target.value)}
                  >
                    <option value="">Select withholding tax</option>
                    {withholdingTaxCodes.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Material/Service
                  <select
                    className="invoice-edit-modal__select"
                    value={section1.material_service}
                    onChange={(event) => updateSection1("material_service", event.target.value)}
                  >
                    <option value="">Select type</option>
                    <option value="material">Material</option>
                    <option value="service">Service</option>
                  </select>
                </label>
                <label>
                  TDS Value
                  <input
                    className="invoice-edit-modal__input"
                    type="text"
                    value={section1.tds_value}
                    onChange={(event) => updateSection1("tds_value", event.target.value)}
                  />
                </label>
              </div>
              <div className="invoice-edit-modal__actions">
                <button className="primary-button" type="button" onClick={saveSection1} disabled={saving}>
                  Save Section 1
                </button>
              </div>
            </section>

            <section className={`invoice-edit-modal__section ${activeSection === "items" ? "is-visible" : ""}`}>
              <h3>Section 2 - Invoice Items with GST Tax</h3>
              <InvoiceItemsTable items={items} onChangeTaxCode={updateItemTaxCode} gstCodes={gstTaxCodes} />
              <div className="invoice-edit-modal__actions">
                <button className="primary-button" type="button" onClick={saveSection2} disabled={saving}>
                  Save Section 2
                </button>
              </div>
            </section>

            <section
              className={`invoice-edit-modal__section ${
                activeSection === "cost" ? "is-visible" : ""
              }`}
            >
              <h3>Section 3 - Unplanned Cost</h3>

              {(() => {
                const invoiceValue = Number(section3.InvoiceValue || 0);
                const invoiceValueMigo = Number(section3.InvoiceValueMigo || 0);
                const unallocatedCost_ = Number(section3.unallocatedCost || 0);

                const _maxallowed = invoiceValue - invoiceValueMigo - unallocatedCost_;

                const processorConfig =
                  unplannedForm?.unallocated_cost_config?.processor || {};

                const thresholdAmount = Number(
                  processorConfig.threshold_amount || 0
                );

                const thresholdPercent = Number(
                  processorConfig.threshold_percent || 0
                );

                const thresholdPercentValue =
                  (invoiceValue * thresholdPercent) / 100;

                let maxAllowedAmount = 0;

                if (processorConfig.condition === "OR") {
                  maxAllowedAmount = Math.min(
                    thresholdAmount,
                    thresholdPercentValue
                  );
                } else {
                  maxAllowedAmount = Math.max(
                    thresholdAmount,
                    thresholdPercentValue
                  );
                }

                // Final maximum allowed
                maxAllowedAmount = Math.min(maxAllowedAmount, _maxallowed);

                const invoiceEligible =
                  maxAllowedAmount > 0;

                const thresholdConfigured =
                  thresholdAmount > 0 ||
                  thresholdPercent > 0;

                const disableForm =
                  !invoiceEligible ||
                  !thresholdConfigured;

                const enteredAmount = Number(
                  unplannedValues.amount || 0
                );

                return (
                  <>
                    <div className="invoice-edit-modal__summary">
                      <div className="invoice-edit-modal__summary-card">
                        <span>Invoice Value (OCR)</span>
                        <strong>{section3.InvoiceValue}</strong>
                      </div>

                      <div className="invoice-edit-modal__summary-card">
                        <span>Invoice Value (MIGO)</span>
                        <strong>{section3.InvoiceValueMigo}</strong>
                      </div>

                      <div className="invoice-edit-modal__summary-card">
                        <span>Unallocated Cost</span>
                        <strong>{unallocatedCost_}</strong>
                      </div>

                      <div className="invoice-edit-modal__summary-card">
                        <span>Diffrence</span>
                        <strong>{_maxallowed}</strong>
                      </div>

                      
                    </div>

                    {!invoiceEligible && (
                      <div className="invoice-edit-modal__warning">
                        Invoice Value must be greater than Invoice Value (MIGO)
                        before Unplanned Cost can be created.
                      </div>
                    )}

                    {!thresholdConfigured && (
                      <div className="invoice-edit-modal__warning">
                        No threshold configured for Processor.
                        Please configure Unplanned Cost settings first.
                      </div>
                    )}

                    {invoiceEligible && thresholdConfigured && (
                      <>
                        <div className="invoice-edit-modal__info">
                          Maximum Allowed Amount:{" "}
                          <strong>{maxAllowedAmount}</strong>
                        </div>

                        <h4 className="invoice-edit-modal__subheading">
                          Unplanned Cost Details
                        </h4>

                        
                        <div className="invoice-edit-modal__grid invoice-edit-modal__grid--unplanned">
                          {/* Amount */}

                          <label>
                            Amount
                            <input
                              className="invoice-edit-modal__input"
                              type="number"
                              min="0"
                              max={maxAllowedAmount}
                              value={unplannedValues.amount || ""}
                              onChange={(event) => {
                                const value = event.target.value;

                                if (value === "") {
                                  updateUnplannedField("amount", "");
                                  return;
                                }

                                const numericValue = Math.min(Number(value), maxAllowedAmount);

                                updateUnplannedField("amount", numericValue);
                              }}
                            />
                          </label>

                          {/* Description */}

                          <label>
                            Description
                            <input
                              className="invoice-edit-modal__input"
                              type="text"
                              value={
                                unplannedValues.item_descritption || ""
                              }
                              onChange={(event) =>
                                updateUnplannedField(
                                  "item_descritption",
                                  event.target.value
                                )
                              }
                            />
                          </label>

                          {/* Tax Code */}

                          <label>
                            Tax Code
                            <select
                              className="invoice-edit-modal__select"
                              value={unplannedValues.TaxCode || ""}
                              onChange={(event) =>
                                updateUnplannedField(
                                  "TaxCode",
                                  event.target.value
                                )
                              }
                            >
                              <option value="">
                                Select Tax Code
                              </option>

                              {(unplannedForm?.gst_taxcode_list || []).map(
                                (code) => (
                                  <option
                                    key={code}
                                    value={code}
                                  >
                                    {code}
                                  </option>
                                )
                              )}
                            </select>
                          </label>

                          {/* Withholding Tax */}

                          <label>
                            Withholding Tax Code
                            <select
                              className="invoice-edit-modal__select"
                              value={
                                unplannedValues.WithholdingTaxCode || ""
                              }
                              onChange={(event) =>
                                updateUnplannedField(
                                  "WithholdingTaxCode",
                                  event.target.value
                                )
                              }
                            >
                              <option value="">
                                Select Tax Code
                              </option>

                              {(unplannedForm?.wtaxcode_list || []).map(
                                (code) => (
                                  <option
                                    key={code}
                                    value={code}
                                  >
                                    {code}
                                  </option>
                                )
                              )}
                            </select>
                          </label>

                          {/* GL Account */}

                          {unplannedForm?.unallocated_cost_config
                            ?.add_to_material_service === "Y" ? (
                            <label>
                              GL Account
                              <input
                                className="invoice-edit-modal__input"
                                type="text"
                                value={
                                  unplannedValues.GLAccount || ""
                                }
                                readOnly
                              />
                            </label>
                          ) : (
                            <label>
                              GL Account
                              <select
                                className="invoice-edit-modal__select"
                                value={
                                  unplannedValues.GLAccount || ""
                                }
                                onChange={(event) =>
                                  updateUnplannedField(
                                    "GLAccount",
                                    event.target.value
                                  )
                                }
                              >
                                <option value="">
                                  Select GL Code
                                </option>

                                {(unplannedForm?.gl_list || []).map(
                                  (gl) => (
                                    <option
                                      key={gl.code}
                                      value={gl.code}
                                    >
                                      {gl.description
                                        ? `${gl.code} (${gl.description})`
                                        : gl.code}
                                    </option>
                                  )
                                )}
                              </select>
                            </label>
                          )}
                        </div>
                        <h4 className="invoice-edit-modal__subheading">
                          GRN Information
                        </h4>
                        <div className="invoice-edit-modal__grid invoice-edit-modal__grid--unplanned">

                          {/* Dynamic GRN Fields */}
                          
                          {Object.values(
                            unplannedForm?.grn_fields || {}
                          ).map((field) => (
                            <label key={`grn-${field.field}`}>
                              {field.field}
                              <input
                                className="invoice-edit-modal__input"
                                type="text"
                                value={
                                  unplannedValues[field.field] || ""
                                }
                                onChange={(event) =>
                                  updateUnplannedField(
                                    field.field,
                                    event.target.value
                                  )
                                }
                              />
                            </label>
                          ))}
                        </div>
                        <h4 className="invoice-edit-modal__subheading">
                          PO Information
                        </h4>
                        <div className="invoice-edit-modal__grid invoice-edit-modal__grid--unplanned">
                          {/* Dynamic PO Fields */}
                        
                          {Object.values(
                            unplannedForm?.po_fields || {}
                          ).map((field) => (
                            <label key={`po-${field.field}`}>
                              {field.field}
                              <input
                                className="invoice-edit-modal__input"
                                type="text"
                                value={
                                  unplannedValues[field.field] || ""
                                }
                                onChange={(event) =>
                                  updateUnplannedField(
                                    field.field,
                                    event.target.value
                                  )
                                }
                              />
                            </label>
                          ))}
                        </div>
                        

                        {enteredAmount > maxAllowedAmount && (
                          <div className="invoice-edit-modal__warning">
                            Maximum allowed amount is{" "}
                            {maxAllowedAmount}
                          </div>
                        )}

                        <div className="invoice-edit-modal__actions">
                          <button
                            className="primary-button"
                            type="button"
                            onClick={saveSection3}
                            disabled={
                              saving ||
                              enteredAmount > maxAllowedAmount ||
                              disableForm
                            }
                          >
                            Save Unplanned Cost
                          </button>
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </section>

            <section className={`invoice-edit-modal__section ${activeSection === "exchange" ? "is-visible" : ""}`}>
              <h3>Section 4 - Exchange Rate</h3>
              <div className="invoice-edit-modal__grid">
                <label>
                  Exchange Rate (MIGO)
                  <input className="invoice-edit-modal__input" type="text" value={section4.ExchangeRate} readOnly />
                </label>
                <label>
                  Exchange Rate (API)
                  <input className="invoice-edit-modal__input" type="text" value={section4.CurrentExchangeRate} readOnly />
                </label>
                <label>
                  Exchange Rate (User)
                  <input
                    className="invoice-edit-modal__input"
                    type="text"
                    value={section4.exchange_rate_user}
                    onChange={(event) => updateSection4("exchange_rate_user", event.target.value)}
                  />
                </label>
              </div>
              <div className="invoice-edit-modal__actions">
                <button className="primary-button" type="button" onClick={saveSection4} disabled={saving}>
                  Save Section 4
                </button>
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
}
